import { useMutation, useQuery } from '@vue/apollo-composable';
import { QueryHeatMapUrlListInput } from '@models/insight/useTopHeatmapList';
import gql from 'graphql-tag';

type PeriodEnum = 'MONTHLY';

export interface PteAutoSaveHistoryHeatmapConfigInput {
    profileId: string;
    siteId: string;
    isMergeParameter: boolean;
    pageGroupId: string;
    type: number;
    uid: string;
    url: string;
    period: PeriodEnum;
    periodValue: number;
    isEnabled: boolean;
}

export interface PteShareHeatmapConfigInput {
    profileId: string;
    siteId: string;
    url: string;
    calendarEnabled: boolean;
    historyHeatmapEnabled: boolean;
}

export interface PtiConversionList {
    conversionId: string;
    cvName: string;
}

export interface ConversionInput {
    conversionId: String;
    cvName: String;
}

export interface HomeSelectedConversionInput {
    profileId: string;
    conversions: ConversionInput[];
}

export interface PtiConversionDataResult {
    cv: number;
    cvRate: string;
    conversionId: string;
    cvName: string;
}

const CREATE_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_MUTATION = gql`
    mutation createAutoSaveHistoryHeatmapConfig($input: PteAutoSaveHistoryHeatmapConfigInput!) {
        createAutoSaveHistoryHeatmapConfig(input: $input)
    }
`;

const CREATE_SHARE_HEATMAP_CONFIG_MUTATION = gql`
    mutation createShareHeatmapConfig($input: PteShareHeatmapConfigInput!) {
        createShareHeatmapConfig(input: $input)
    }
`;

const GET_SHARE_HEATMAP_CONFIG_BY_URL_QUERY = gql`
    query getShareHeatmapConfigByUrl($url: String!) {
        getShareHeatmapConfigByUrl(url: $url) {
            profileId
            siteId
            url
            calendarEnabled
            historyHeatmapEnabled
        }
    }
`;

const GET_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_BY_URL_QUERY = gql`
    query getAutoSaveHistoryHeatmapConfigByUrl($url: String!) {
        getAutoSaveHistoryHeatmapConfigByUrl(url: $url) {
            profileId
            siteId
            isMergeParameter
            pageGroupId
            type
            uid
            url
            period
            periodValue
            isEnabled
        }
    }
`;

const GET_CONVERSION_LIST = gql`
    query getHomeConversionList($profileId: String!) {
        getHomeConversionList(profileId: $profileId) {
            conversionId
            cvName
        }
    }
`;

const GET_CONVERSION_DATA = gql`
    query getHomeConversionMetrics($input: HomeMetricsQueryInput!) {
        getHomeConversionMetrics(input: $input) {
            cv
            cvRate
            conversionId
            cvName
        }
    }
`;

const SAVE_CONVERSION = gql`
    mutation saveHomeSelectedConversionList($input: HomeSelectedConversionInput!) {
        saveHomeSelectedConversionList(input: $input)
    }
`;

const HOME_URL_TERMINAL_DETAIL = gql`
    query getHomeUrlTerminalDetail($input: HeatMapUrlListInput!) {
        getHomeUrlTerminalDetail(input: $input) {
            pc
            smartphone
            tablet
            other
        }
    }
`;

export function useHeatmap() {
    const createShareHeatmapConfig = async (
        params: PteShareHeatmapConfigInput,
        resultCallback?: (status: boolean) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { mutate } = getCreateShareHeatmapConfigMutation();
        const res = await mutate({
            input: params
        });

        if (res?.data?.createShareHeatmapConfig) {
            resultCallback && typeof resultCallback === 'function' && resultCallback(res.data);
        } else {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(res?.data);
        }
    };

    const getShareHeatmapConfigByUrl = (
        heatmapPageUrl: string,
        resultCallback?: (T: PteShareHeatmapConfigInput) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getShareHeatmapConfigByUrl: PteShareHeatmapConfigInput;
        }>(
            GET_SHARE_HEATMAP_CONFIG_BY_URL_QUERY,
            {
                url: heatmapPageUrl
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data.data.getShareHeatmapConfigByUrl);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const createAutoSaveHistoryHeatmapConfig = async (
        params: PteAutoSaveHistoryHeatmapConfigInput,
        resultCallback?: (status: boolean) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { mutate } = getCreateAutoSaveHistoryHeatmapConfigMutation();
        const res = await mutate({
            input: params
        });

        if (res?.data?.createAutoSaveHistoryHeatmapConfig) {
            resultCallback && typeof resultCallback === 'function' && resultCallback(res.data);
        } else {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(res?.data);
        }
    };

    const getAutoSaveHistoryHeatmapConfigByUrl = (
        heatmapPageUrl: string,
        resultCallback?: (T: PteAutoSaveHistoryHeatmapConfigInput) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getAutoSaveHistoryHeatmapConfigByUrl: PteAutoSaveHistoryHeatmapConfigInput;
        }>(
            GET_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_BY_URL_QUERY,
            {
                url: heatmapPageUrl
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data.data.getAutoSaveHistoryHeatmapConfigByUrl);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const getPtiConversionList = (
        profileId: string,
        resultCallback?: (T: PtiConversionList[]) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getHomeConversionList: PtiConversionList[];
        }>(
            GET_CONVERSION_LIST,
            {
                profileId
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data?.data?.getHomeConversionList || []);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const getPtiConversionData = (
        uid: string,
        profileId: string,
        startTime: string, // "2022/06/01"
        endTime: string, // "2022/06/01"
        resultCallback?: (T: PtiConversionDataResult[]) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getHomeConversionMetrics: PtiConversionDataResult[];
        }>(
            GET_CONVERSION_DATA,
            {
                input: {
                    profileId,
                    accountId: uid,
                    startTime,
                    endTime
                }
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );

        onResult(data => {
            if (data?.data?.getHomeConversionMetrics) {
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(data?.data?.getHomeConversionMetrics || []);
            }
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    return {
        createShareHeatmapConfig,
        getShareHeatmapConfigByUrl,
        getPtiConversionList,
        getPtiConversionData,
        createAutoSaveHistoryHeatmapConfig,
        getAutoSaveHistoryHeatmapConfigByUrl
    };
}

export async function saveConversionList(
    uid: string,
    profileId: string,
    conversions: PtiConversionList[]
) {
    const { mutate } = useMutation(SAVE_CONVERSION, {
        fetchPolicy: 'no-cache'
    });
    const res = await mutate({
        input: {
            accountId: uid,
            profileId,
            conversions
        }
    });
    return res;
}

function getCreateAutoSaveHistoryHeatmapConfigMutation() {
    return useMutation(CREATE_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_MUTATION, {
        fetchPolicy: 'no-cache'
    });
}

function getCreateShareHeatmapConfigMutation() {
    return useMutation(CREATE_SHARE_HEATMAP_CONFIG_MUTATION, {
        fetchPolicy: 'no-cache'
    });
}

export async function getHomeUrlTerminalDetail(
    params?: QueryHeatMapUrlListInput,
    resultCallback?: (T: QueryHeatMapUrlListInput) => void,
    rejectCallback?: (err: any) => void
) {
    const {
        onError,
        onResult
    } = useQuery<{ getHomeUrlTerminalDetail: QueryHeatMapUrlListInput }>(
        HOME_URL_TERMINAL_DETAIL,
        { input: params },
        {
            fetchPolicy: 'no-cache' //cache-and-network cache-first
        }
    );
    onResult(data => {
        resultCallback &&
            typeof resultCallback === 'function' &&
            resultCallback(data?.data?.getHomeUrlTerminalDetail as QueryHeatMapUrlListInput);
    });

    onError(err => {
        rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
    });
}
